<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import logo from "@/assets/logo-piplanning.svg";
import { webappUrl } from "@/environment";
import { getFeatureQuery } from "@/feature";
import { useAppStore } from "@/stores";
import { daysUntil } from "@/utils/dates";

import SidebarLink from "./SidebarLink.vue";
import SvgIcon from "./SvgIcon.vue";
import TrialExtensionCard from "./TrialExtensionCard.vue";
import UsageCard from "./UsageCard.vue";
import UserMenu from "./UserMenu.vue";

const route = useRoute();

const { isAdmin, isPlanningIntervalAdmin, isTraining, license } = storeToRefs(useAppStore());

const orgExpanded = ref(false);

const expiring = computed(() => {
  const expireDays = daysUntil(license.value.expiryDate);
  return expireDays !== null && expireDays < 30;
});
const isOrgActive = computed(() => route.path.startsWith("/organization"));

watch(
  isOrgActive,
  (val) => {
    if (val) orgExpanded.value = true;
  },
  { immediate: true },
);

function query() {
  return getFeatureQuery(route);
}
</script>

<template>
  <nav class="sidebar" aria-label="Main navigation">
    <div class="menu">
      <div class="sidebar-brand">
        <img :src="logo" alt="piplanning.io logo" />
      </div>

      <a :href="webappUrl" target="_blank" rel="noopener noreferrer" class="app-link">
        <span>piplanning app</span>
        <SvgIcon name="general/external-link" width="16" height="16" />
      </a>

      <div class="menu-scroll">
        <ul class="menu-items-group">
          <SidebarLink :to="{ name: 'home', query: query() }">
            <SvgIcon name="system/home" width="20" height="20" />
            Home
          </SidebarLink>

          <SidebarLink
            :to="{ name: 'safepiplannings', query: query() }"
            :related-routes="['editSession', 'addnewsafepiplanning']"
          >
            <SvgIcon name="general/pi" width="20" height="20" />
            PI Sessions
          </SidebarLink>

          <li
            v-if="isAdmin || isPlanningIntervalAdmin"
            class="sidebar-group"
            :class="{
              'sidebar-group-active': isOrgActive,
              'sidebar-group-open': orgExpanded,
            }"
          >
            <button
              class="group-toggle"
              :aria-expanded="orgExpanded"
              @click="orgExpanded = !orgExpanded"
            >
              <SvgIcon name="core/organization" width="20" height="20" />
              Organization
              <SvgIcon
                name="system/chevron-down"
                width="22"
                height="22"
                class="chevron"
                :class="{ 'chevron-open': orgExpanded }"
                aria-hidden="true"
              />
            </button>
            <ul v-if="orgExpanded" class="group-children">
              <SidebarLink plain :to="{ name: 'organization_tree', query: query() }">
                Overview
              </SidebarLink>
              <SidebarLink plain :to="{ name: 'teams', query: query() }"> Teams </SidebarLink>
              <SidebarLink plain :to="{ name: 'arts', query: query() }"> ARTs </SidebarLink>
              <SidebarLink plain :to="{ name: 'solution_trains', query: query() }">
                Solution Trains
              </SidebarLink>
              <SidebarLink plain :to="{ name: 'external_orgs', query: query() }">
                External Orgs
              </SidebarLink>
            </ul>
          </li>

          <SidebarLink :to="{ name: 'users', query: query() }">
            <SvgIcon name="persona/team" width="20" height="20" />
            Users
          </SidebarLink>
        </ul>
        <ul class="menu-items-group">
          <SidebarLink :to="{ name: 'almConnections', query: query() }">
            <SvgIcon name="general/alm-connection" width="20" height="20" />
            ALM Connections
          </SidebarLink>

          <SidebarLink
            v-if="isAdmin && !isTraining"
            :to="{ name: 'SelectIdentityProvider', query: query() }"
            not-allowed-title="SSO is part of our premium plan"
          >
            <SvgIcon name="core/shield" width="20" height="20" />
            SSO
          </SidebarLink>

          <SidebarLink
            :class="{ highlight: expiring }"
            :to="{ name: isTraining ? 'trainingBilling' : 'billing', query: query() }"
          >
            <SvgIcon name="general/billing" width="20" height="20" />
            Billing
          </SidebarLink>

          <SidebarLink :to="{ name: 'settings', query: query() }">
            <SvgIcon name="system/settings" width="20" height="20" />
            Settings
          </SidebarLink>
        </ul>
      </div>
      <div class="sidebar-bottom">
        <TrialExtensionCard />
        <UsageCard />

        <div class="sidebar-footer">
          <UserMenu />
        </div>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;

.sidebar {
  background-color: c.$background-grey-light;
  width: 100%;
  position: sticky;
  top: 0;
  height: 100vh;
  align-self: start;
  overflow: hidden;
  z-index: 2;

  .menu {
    padding: 6px 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    height: 100%;
    box-sizing: border-box;

    .sidebar-brand {
      padding: 8px 12px;

      img {
        width: 136px;
        height: auto;
      }
    }

    .app-link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 9px 12px;
      font-weight: 500;
      font-size: 14px;
      letter-spacing: 0.01em;
      color: p.$brand-70;
      text-decoration: none;
      border-radius: 6px;
      transition: background-color 100ms ease;

      &:hover {
        background-color: p.$black-alpha-5;
        color: c.$main;
      }
    }

    .menu-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .menu-items-group {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      &:first-of-type {
        padding-top: 8px;
        border-top: 1px solid c.$border-subtle;
      }
    }

    .sidebar-group {
      display: block;
      width: 100%;
      padding: 0;
      margin: 0;
      border-radius: 6px;

      &.sidebar-group-active > .group-toggle {
        color: c.$main;
        font-weight: 600;
      }

      &.sidebar-group-open > .group-toggle {
        color: c.$main;
      }

      .group-toggle {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        padding: 9px 12px;
        width: 100%;
        background: none;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        text-align: left;
        font-size: 14px;
        font-weight: 500;
        color: p.$brand-70;
        letter-spacing: 0.01em;

        &:hover:not(:disabled) {
          background-color: p.$black-alpha-5;
          color: c.$main;
        }

        .chevron {
          margin-left: auto;
          transition: transform 0.2s ease;
          flex-shrink: 0;

          &.chevron-open {
            transform: rotate(180deg);
          }
        }
      }

      .group-children {
        margin: 0 0 0 14px;
        padding-left: 8px;

        > li {
          position: relative;

          &::before {
            content: "";
            position: absolute;
            left: -3px;
            top: 50%;
            transform: translateY(-50%);
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: c.$border;
            z-index: 1;
          }

          &::after {
            content: "";
            position: absolute;
            left: -1px;
            top: 0;
            bottom: 0;
            width: 1px;
            background-color: c.$border;
          }

          &:first-child::after {
            top: 50%;
          }

          &:last-child::after {
            bottom: 50%;
          }
        }
      }
    }

    .sidebar-bottom {
      flex-shrink: 0;
      padding-top: 12px;
      display: flex;
      flex-direction: column;
      gap: 12px;

      .sidebar-footer {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
    }
  }
}
</style>
